import React, { useEffect, useRef, useState } from 'react';
import useAppSettings from '@afosto/utils-react/app/useAppSettings';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useLocalStorage from '@afosto/utils-react/useLocalStorage';
import { useSnackbar } from 'notistack';
import { useAppActions, useAppState } from '@afosto/app-components-react/AppProvider';
import { Switch, Route, Redirect } from 'react-router-dom';
import CreateShipment from '../pages/CreateShipment';
import EditShipment from '../pages/EditShipment';
import FulfilmentLocationsDialog from '../components/FulfilmentLocationsDialog';
import HandlingLists from '../pages/HandlingLists/HandlingLists';
import Packing from '../pages/Packing';
import PackingView from '../pages/PackingView';
import Parcels from '../pages/Parcels';
import ParkParcels from '../pages/ParkParcels';
import ProcessHandlingList from '../pages/ProcessHandlingList';
import Settings from '../pages/Settings';
import Shipments from '../pages/Shipments/Shipments';
import ViewFulfilmentLocation from '../pages/ViewFulfilmentLocation';
import ViewHandlingList from '../pages/ViewHandlingList';
import ViewParcel from '../pages/ViewParcel';
import ViewShipment from '../pages/ViewShipment';
import UnpackingView from '../pages/UnpackingView';
import AppLayout from '@afosto/components-react/AppLayout';
import AppWorkspace from '@afosto/components-react/AppWorkspace';
import BatchesPage from '@afosto/app-components-react/BatchesPage';
import Dock from '@afosto/app-components-react/Dock';
import Drawer from '@afosto/app-components-react/Drawer';
import DrawerMenu from '@afosto/app-components-react/DrawerMenu';
import wait from '@afosto/utils/wait';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import defaultSettings from '../data/defaultSettings';
import menu from '../data/navigationMenu';
import { FULFILMENT_LOCATIONS_KEY } from '../utils/constants';
import translations from './translations';

const Root = () => {
  const intl = useIntl();
  const { appKey, settings } = useAppState();
  const [showLocationsDialog, setShowLocationsDialog] = useState(false);
  const [getLocalStorage, setLocalStorage] = useLocalStorage();
  const [actions] = useAppActions();
  const { enqueueSnackbar } = useSnackbar();
  const intlRef = useRef(intl);
  const setSettingsRef = useRef(null);
  const settingsRef = useRef(null);

  useAppSettings(`${appKey}.shipments`, defaultSettings);

  useEffect(() => {
    setSettingsRef.current = actions.setSettings;
    settingsRef.current = settings;
  });

  useEffect(() => {
    let isMounted = true;

    const checkLocation = async (retry = true) => {
      try {
        const response = await fulfilmentApi.listFulfilmentLocations();
        const { data = [] } = response ?? {};

        const existingLocationIds = data.map(location => location.id);
        const locationIds = getLocalStorage(FULFILMENT_LOCATIONS_KEY);
        const selectedLocationIds = locationIds?.length > 0 ? JSON.parse(locationIds) : [];
        const filteredLocationIds = selectedLocationIds.filter(id =>
          existingLocationIds.includes(id),
        );

        if (isMounted) {
          setSettingsRef.current({
            ...settingsRef.current,
            locations: filteredLocationIds,
          });

          if (!filteredLocationIds?.length && data?.length > 0) {
            setShowLocationsDialog(true);
          }
        }
      } catch (error) {
        if (retry) {
          await wait(1000);
          await checkLocation(false);
        } else {
          enqueueSnackbar(
            intlRef.current.formatMessage(translations.feedback.fetchLocationsFailed),
            {
              variant: 'error',
            },
          );
          setShowLocationsDialog(true);
        }
      }
    };

    checkLocation();

    return () => {
      isMounted = false;
    };
  }, [enqueueSnackbar, getLocalStorage]);

  const handleSelectLocations = ({ locations }) => {
    const locationIds = (locations ?? []).map(location => location.id);
    actions.setSettings({ ...settings, locations: locationIds });
    setLocalStorage(FULFILMENT_LOCATIONS_KEY, JSON.stringify(locationIds));
    setShowLocationsDialog(false);
  };

  return (
    <AppLayout>
      <Drawer>
        <Dock />
        <DrawerMenu title={intl.formatMessage(translations.drawer.appTitle)} menu={menu} />
      </Drawer>
      <AppWorkspace>
        <Switch>
          <Route path="/parcels/:parcelID" component={ViewParcel} />
          <Route path="/park-parcels" component={ParkParcels} />
          <Route path="/parcels" component={Parcels} />
          <Route
            path="/settings/fulfilment-locations/:fulfilmentLocationID"
            component={ViewFulfilmentLocation}
          />
          <Route path="/settings" component={Settings} />
          <Route
            path="/batches"
            render={() => <BatchesPage actionsCode={appKey} excludeActions={['position_export']} />}
          />
          <Route path="/unpacking/:shipmentID" component={UnpackingView} />
          <Route path="/packing/:shipmentID/:parcelID?" component={PackingView} />
          <Route path="/packing/:handlingListID?" component={Packing} />
          <Route
            path="/handling-lists/:handlingListID/process/:secondAction?"
            component={ProcessHandlingList}
          />
          <Route path="/handling-lists/:handlingListID" component={ViewHandlingList} />
          <Route exact path="/handling-lists" component={HandlingLists} />
          <Route path="/shipments/new" component={CreateShipment} />
          <Route path="/shipments/edit/:id" component={EditShipment} />
          <Route path="/shipments/:id" component={ViewShipment} />
          <Route exact path="/shipments" component={Shipments} />
          <Redirect to="/shipments" />
        </Switch>
      </AppWorkspace>
      <FulfilmentLocationsDialog
        isOpen={showLocationsDialog}
        onSubmit={handleSelectLocations}
        disableRestoreFocus
      />
    </AppLayout>
  );
};

export default Root;
